import { Box } from '@lifeomic/chroma-react/components/Box';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { TableModuleActions } from '@lifeomic/chroma-react/components/TableModule/';
import { useStyles as useTableModuleStyles } from '@lifeomic/chroma-react/components/TableModule/TableModule';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { ContainerQuery } from 'react-container-query';
import { defineMessages, useIntl } from 'react-intl';
import { usePrevious } from 'react-use';
import { useNotify } from '../../../../../hooks';
import { useSwitchToTemplatingItem } from '../../../contexts/templatingItemSwitch';
import { useAITemplateInvocations } from '../../../hooks/useAITemplateInvocations';
import { useSelectedTemplatingItem } from '../../../hooks/useSelectedTemplatingItem';
import {
  CompositeReportDefinitionSummary,
  ModelUtils,
  TemplateSummary,
} from '../../../models';
import { AddTemplateToCRD } from '../../Buttons/AddTemplateToCRD';
import { CopyTemplatingItemButton } from '../../Buttons/CopyTemplatingItemButton';
import { DeleteTemplatingItemButton } from '../../Buttons/DeleteTemplatingItemButton';
import { EditTemplatingItemMetadataButton } from '../../Buttons/EditTemplatingItemMetadataButton';
import { TemplateIcon } from '../../Icons/TemplateIcon';
import { useCommonStyles } from '../../styles';

const messages = defineMessages({
  generationCompleted: {
    id: 'TemplateNode.generationCompleted',
    defaultMessage: 'Generation completed for {templateName}',
  },
});

const useStyles = makeStyles<{
  level: number;
  isSelected: boolean;
}>(theme => ({
  container: {
    border: 'none',
  },
  templateButton: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    borderRadius: 4,
    marginLeft: props =>
      theme.spacing(props.level > 0 ? props.level * 3 + 1 : 1),
    marginRight: theme.spacing(1),
    backgroundColor: props =>
      props.isSelected ? theme.palette.action.selected : 'transparent',
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  templateName: {
    overflow: 'hidden',
    flexGrow: 1,
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  templateAuthor: {
    marginRight: theme.spacing(2),
  },
  tableModuleActions: {
    minWidth: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    // override the left: 0 from the tableModuleStyles which is high specificity
    left: 'unset!important',
    margin: theme.spacing(0.5),
  },
}));

const query = {
  isNarrow: {
    maxWidth: 500,
  },
};

interface TemplateNodeProps {
  template: TemplateSummary;
  templateNodeActions?: (data: TemplateSummary) => React.ReactNode;
  compositeReportDefinitionNodeActions?: (
    data: CompositeReportDefinitionSummary,
  ) => React.ReactNode;
  className?: string;
}

export const TemplateNode: React.FC<TemplateNodeProps> = ({
  template,
  templateNodeActions,
}) => {
  const { isSelected } = useSelectedTemplatingItem();
  const { invocations } = useAITemplateInvocations();
  const intl = useIntl();
  const styles = useStyles({
    level: ModelUtils.getLevel(template),
    isSelected: isSelected(template),
  });
  const commonStyles = useCommonStyles();
  const tableModuleStyles = useTableModuleStyles();
  const { switchToTemplatingItem } = useSwitchToTemplatingItem();

  const templateName = ModelUtils.getName(template);

  const handleClick = () => switchToTemplatingItem(template);

  const isGenerating = useMemo(() => {
    const invocation =
      invocations[template.id] || invocations[template.durableId];
    return invocation && !invocation.isResolved;
  }, [invocations, template.id, template.durableId]);

  const previousIsGenerating = usePrevious(isGenerating);
  const [notify] = useNotify();

  useEffect(() => {
    if (previousIsGenerating && !isGenerating) {
      const message = intl.formatMessage(messages.generationCompleted, {
        templateName,
      });
      notify(message, 'success');
    }
  }, [previousIsGenerating, isGenerating, notify, intl, templateName]);

  const actions = useMemo(() => {
    return templateNodeActions?.(template);
  }, [templateNodeActions, template]);

  return (
    <Box
      className={clsx(
        tableModuleStyles.tableDataRow,
        commonStyles.fileSystemNode,
        styles.container,
      )}
      fullWidth
    >
      <ContainerQuery query={query}>
        {({ isNarrow }) => (
          <Tooltip title={templateName} placement="left">
            <ButtonUnstyled
              className={styles.templateButton}
              onClick={handleClick}
            >
              <TemplateIcon isGenerating={isGenerating} template={template} />
              <Text size="table" className={styles.templateName}>
                {templateName}
              </Text>
              {isNarrow ? null : (
                <Text size="caption" className={styles.templateAuthor}>
                  {template.lastUpdatedBy}
                </Text>
              )}
              <TableModuleActions
                className={clsx(
                  tableModuleStyles.tableModuleActions,
                  styles.tableModuleActions,
                )}
              >
                {actions ? (
                  actions
                ) : (
                  <>
                    <AddTemplateToCRD template={template} />
                    <CopyTemplatingItemButton templatingItem={template} />
                    <EditTemplatingItemMetadataButton
                      templatingItem={template}
                    />
                    <DeleteTemplatingItemButton templatingItem={template} />
                  </>
                )}
              </TableModuleActions>
            </ButtonUnstyled>
          </Tooltip>
        )}
      </ContainerQuery>
    </Box>
  );
};
